/**
 * @fileoverview useCurrentTeamMember hook
 *
 * Returns the current user's Airtable Team record from CurrentUserContext.
 * Use this when you need the canonical RSH team member name (from
 * FIELDS.TEAM.NAME), not the Firebase Auth `displayName`.
 *
 * Falls back to `localStorage.userData` (populated by App.js on sign-in)
 * when called outside a CurrentUserContext.Provider — keeps the hook
 * safe for any admin component that may render before the provider is
 * mounted, and avoids a hard failure if App.js ever fetches userData
 * without a surrounding provider.
 *
 * @version 1.0.0
 */
import { useContext } from "react";
import CurrentUserContext from "../contexts/CurrentUserContext";

/**
 * Read the current Team member record from local storage as a fallback
 * when context isn't available.
 *
 * @returns {Object|null} Parsed userData, or null if missing/malformed
 */
const readTeamMemberFromStorage = () => {
  try {
    const raw =
      typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem("userData")
        : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

/**
 * Access the current user's Airtable Team record.
 *
 * Prefer `teamMember.name` over Firebase Auth `displayName` for any
 * user-attributed metadata (uploads, comments, activity logs).
 *
 * @returns {{
 *   teamMember: Object|null,
 *   name: string,
 *   email: string,
 *   role: string,
 *   id: string,
 * }} Team member fields with safe string defaults
 *
 * @example
 * const { name } = useCurrentTeamMember();
 * const uploadContext = buildUploadContext({ ..., uploadedByName: name });
 */
const useCurrentTeamMember = () => {
  const { userData } = useContext(CurrentUserContext);
  const teamMember = userData || readTeamMemberFromStorage();

  return {
    teamMember,
    name: teamMember?.name || "",
    email: teamMember?.email || "",
    role: teamMember?.role || "",
    id: teamMember?.id || "",
  };
};

export default useCurrentTeamMember;
